/* ============================================================
 * Quick Build — expanding step-picker strip (MULTI-INSTANCE)
 * ------------------------------------------------------------
 * Charlie 2026-05-21 (refactored from single-instance original)
 *
 * Now supports >1 Quick Build strip per page. The homepage uses two:
 *   1. quick_build_hero   — opened by the hero "Build Yours" CTA.
 *      Reveals build_your_chuug_3GJy6B (the first hidden BYC).
 *   2. quick_build_social — opened by the new BUILD YOURS button at
 *      the bottom of the social-proof gallery. Reveals
 *      build_your_chuug_Xy3XzL (the second hidden BYC).
 *
 * Wiring rules (no DOM gymnastics in templates required):
 *   - Each strip element has `data-qb-instance="<key>"` (set to
 *     section.settings.instance_key, else section.id).
 *   - Each strip is paired with a hidden BYC. Pairing strategy:
 *       a) Explicit: `data-qb-target-byc="<byc-section-id>"` on strip.
 *       b) Fallback: the next `[data-build-hidden="1"]` after the strip
 *          in DOM order. (Used for both instances on the homepage today.)
 *   - Triggers are any element with `data-qb-trigger="<instance-key>"`.
 *     Plus, for backward compatibility, all `.hero a[href="#build"]`
 *     CTAs auto-bind to the FIRST strip on the page (the hero one).
 *
 * Each instance maintains its own state object, its own cloned option
 * cards, its own summary pills, its own BUILD button. Picking a wood in
 * one strip does NOT affect the other. Only the strip's paired hidden
 * BYC is revealed/populated on BUILD.
 * ============================================================ */
(function(){
  'use strict';

  // Guard against double-init when the asset is included twice (e.g.
  // theme editor reloads). Bumped tag because the contract changed.
  if (window.__QB_INIT_V2__) return;
  window.__QB_INIT_V2__ = true;

  function ready(fn){
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // Coin preview CDN (mirrors the master configurator's URL pattern).
  var COIN_BASE = 'https://axqytwventrsvgtrxcwd.supabase.co/storage/v1/object/public/brand-assets/coins/';
  // Charlie 2026-06-04: bumped v8 -> v9 with the launch of the DAD word coin.
  // v9 -> v10: DAD.jpg re-centred so the disc sits at the same canvas position
  // and same scale as the A-Z letter coins (was sitting high-left in the round
  // BYC aperture because of the asymmetric shadow). Bump again whenever the
  // coin set / artwork changes.
  var COIN_VER  = '?v=10';
  function coinUrl(id){ return COIN_BASE + id + '.jpg' + COIN_VER; }

  function cloneAndClean(node){
    var clone = node.cloneNode(true);
    if (clone.id) clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach(function(el){ el.removeAttribute('id'); });
    // Drop the master's [data-build-*] hooks so its JS can't bind to
    // anything inside the clone if it ever re-runs a querySelector.
    clone.querySelectorAll('[data-build-mainshot],[data-build-mainmedia],[data-build-mainvideo],[data-build-mainimg],[data-build-marker]').forEach(function(el){
      ['data-build-mainshot','data-build-mainmedia','data-build-mainvideo','data-build-mainimg','data-build-marker'].forEach(function(a){ el.removeAttribute(a); });
    });
    return clone;
  }

  function fireChange(el){
    var ev;
    try { ev = new Event('change', { bubbles: true }); }
    catch(_) {
      ev = document.createEvent('HTMLEvents');
      ev.initEvent('change', true, false);
    }
    el.dispatchEvent(ev);
  }
  function fireClick(el){
    var ev;
    try { ev = new MouseEvent('click', { bubbles: true, cancelable: true, view: window }); }
    catch(_) {
      ev = document.createEvent('MouseEvents');
      ev.initEvent('click', true, true);
    }
    el.dispatchEvent(ev);
  }

  // ------------------------------------------------------------------
  // Resolve which hidden BYC pairs with which strip.
  // Explicit `data-qb-target-byc="<section-id>"` wins; else fall back to
  // the next [data-build-hidden] element in document order after the
  // strip. The fallback handles both instances on the homepage today
  // (hero strip → first hidden BYC, social strip → second hidden BYC).
  // ------------------------------------------------------------------
  function resolveHiddenByc(strip){
    var explicit = strip.getAttribute('data-qb-target-byc');
    if (explicit) {
      var byId = document.querySelector('[data-section-id="' + explicit + '"][data-build-hidden]') ||
                 document.querySelector('[data-section-id="' + explicit + '"]');
      if (byId) return byId;
    }
    // DOM-order fallback — walk all hidden BYCs, pick the first one that
    // appears AFTER this strip in the document.
    var allHidden = document.querySelectorAll('[data-build-hidden="1"]');
    for (var i = 0; i < allHidden.length; i++) {
      var pos = strip.compareDocumentPosition(allHidden[i]);
      if (pos & Node.DOCUMENT_POSITION_FOLLOWING) return allHidden[i];
    }
    return null;
  }

  // ------------------------------------------------------------------
  // Per-instance init. All state, listeners, DOM hooks captured in the
  // closure — no leakage between instances.
  // ------------------------------------------------------------------
  function initStrip(strip, isFirstStrip){
    var hiddenByc = resolveHiddenByc(strip);
    if (!hiddenByc) {
      console.warn('[quick-build] Strip', strip.getAttribute('data-qb-instance') || strip,
        'has no paired hidden BYC. Skipping wire-up; any [data-qb-trigger] CTAs for this instance will be no-ops.');
      return;
    }

    var instanceKey = strip.getAttribute('data-qb-instance') || '';
    var ANIM_MS = parseInt(strip.getAttribute('data-anim-ms'), 10) || 2000;

    // Step hosts
    var woodOptsHost = strip.querySelector('[data-qb-options="wood"]');
    var ropeOptsHost = strip.querySelector('[data-qb-options="rope"]');
    var coinHost     = strip.querySelector('[data-qb-coin-host]');

    // --- populate options from the paired hidden BYC ---
    function populateOptions(){
      if (woodOptsHost && !woodOptsHost.children.length) {
        // Charlie 2026-06-05: narrowed from `[data-cway]` to `.cway[data-cway]`
        // so qty-tile buttons (which now carry data-wood / data-rope from
        // the wood+rope tile work) don't get cloned into the wood step.
        var srcWoods = hiddenByc.querySelectorAll('.colourways .cway, .cway[data-cway]');
        srcWoods.forEach(function(src){
          var clone = cloneAndClean(src);
          clone.setAttribute('data-qb-wood', src.getAttribute('data-cway') || '');
          var nameEl = clone.querySelector('.name');
          if (nameEl) clone.setAttribute('data-qb-name', nameEl.textContent.trim());
          woodOptsHost.appendChild(clone);
        });
      }
      if (ropeOptsHost && !ropeOptsHost.children.length) {
        // Charlie 2026-06-05: narrowed from `[data-rope]` to `.rope[data-rope]`
        // so qty-tile buttons (which now carry data-wood / data-rope from
        // the wood+rope tile work) don't get cloned into the rope step.
        var srcRopes = hiddenByc.querySelectorAll('.ropes .rope, .rope[data-rope]');
        srcRopes.forEach(function(src){
          var clone = cloneAndClean(src);
          clone.setAttribute('data-qb-rope', src.getAttribute('data-rope') || '');
          var nameEl = clone.querySelector('.display');
          if (nameEl) clone.setAttribute('data-qb-name', nameEl.textContent.trim());
          ropeOptsHost.appendChild(clone);
        });
      }
      if (coinHost && !coinHost.children.length) {
        var srcCoin = hiddenByc.querySelector('.coin-typer');
        if (srcCoin) {
          var clone = cloneAndClean(srcCoin);
          var qSel = clone.querySelector('[data-coin-select], select');
          if (qSel) {
            qSel.removeAttribute('data-coin-select');
            qSel.setAttribute('data-qb-coin-select', '1');
            qSel.setAttribute('aria-label', 'Choose coin design (Quick Build)');
          }
          var qBg = clone.querySelector('[data-coin-bg], .coin-input-bg');
          if (qBg) {
            qBg.removeAttribute('data-coin-bg');
            qBg.setAttribute('data-qb-coin-bg', '1');
          }
          var qWrap = clone.querySelector('[data-coin-wrap], .coin-preview');
          if (qWrap) qWrap.removeAttribute('data-coin-wrap');
          coinHost.appendChild(clone);
        }
      }
    }
    populateOptions();

    // Clear any inherited `.active` from the master defaults. Mike wants
    // the strip to open with nothing pre-selected.
    function clearClonedSelections(){
      if (woodOptsHost) woodOptsHost.querySelectorAll('.cway.active').forEach(function(el){ el.classList.remove('active'); });
      if (ropeOptsHost) ropeOptsHost.querySelectorAll('.rope.active').forEach(function(el){ el.classList.remove('active'); });
    }
    clearClonedSelections();

    // --- state + nav ---
    // Charlie 2026-06-04 (Mike directive): coin default flipped from 'A' to
    // 'DAD' for the DAD coin launch. Quick Build opens with DAD pre-selected,
    // mirroring the BYC behaviour. coinTouched stays false so the summary
    // line still respects the user's first manual change.
    var state = { wood: null, woodName: null, rope: null, ropeName: null, coin: 'DAD', coinTouched: false };
    var stepOrder = ['wood', 'rope', 'coin'];
    var currentStep = 'wood';

    var backBtn  = strip.querySelector('[data-qb-back]');
    var buildBtn = strip.querySelector('[data-qb-build]');
    var building = strip.querySelector('[data-qb-building]');

    var summary = {
      wood: strip.querySelector('[data-qb-summary-wood]'),
      rope: strip.querySelector('[data-qb-summary-rope]'),
      coin: strip.querySelector('[data-qb-summary-coin]')
    };
    var dots = {
      wood: strip.querySelector('[data-qb-dot="wood"]'),
      rope: strip.querySelector('[data-qb-dot="rope"]'),
      coin: strip.querySelector('[data-qb-dot="coin"]')
    };
    var stepEls = {
      wood: strip.querySelector('[data-qb-step="wood"]'),
      rope: strip.querySelector('[data-qb-step="rope"]'),
      coin: strip.querySelector('[data-qb-step="coin"]')
    };

    function reapplyActiveFor(name){
      if (name === 'wood' && state.wood && woodOptsHost){
        woodOptsHost.querySelectorAll('.cway').forEach(function(x){
          var id = x.getAttribute('data-qb-wood') || x.getAttribute('data-cway');
          x.classList.toggle('active', id === state.wood);
        });
      }
      if (name === 'rope' && state.rope && ropeOptsHost){
        ropeOptsHost.querySelectorAll('.rope').forEach(function(x){
          var id = x.getAttribute('data-qb-rope') || x.getAttribute('data-rope');
          x.classList.toggle('active', id === state.rope);
        });
      }
      if (name === 'coin' && coinHost){
        var sel = coinHost.querySelector('[data-qb-coin-select], select');
        if (sel && state.coin) sel.value = state.coin;
      }
    }

    function showStep(name){
      currentStep = name;
      if (name === 'coin') state.coinTouched = true;
      Object.keys(stepEls).forEach(function(k){
        var el = stepEls[k]; if (!el) return;
        if (k === name) el.removeAttribute('hidden');
        else el.setAttribute('hidden', '');
      });
      Object.keys(dots).forEach(function(k){
        var d = dots[k]; if (!d) return;
        d.classList.remove('is-active', 'is-done');
        if (k === name) d.classList.add('is-active');
        else if (state[k]) d.classList.add('is-done');
      });
      if (backBtn) backBtn.removeAttribute('hidden');
      reapplyActiveFor(name);
      refreshBuildBtn();
      var firstFocus = stepEls[name] && stepEls[name].querySelector('button, select');
      if (firstFocus) { try { firstFocus.focus({ preventScroll: true }); } catch(_) {} }
    }

    function refreshBuildBtn(){
      if (!buildBtn) return;
      if (state.wood && state.rope) buildBtn.removeAttribute('disabled');
      else buildBtn.setAttribute('disabled', '');
    }

    function refreshSummary(){
      if (summary.wood){
        if (state.woodName){ summary.wood.textContent = '01 ' + state.woodName; summary.wood.removeAttribute('hidden'); }
        else summary.wood.setAttribute('hidden','');
      }
      if (summary.rope){
        if (state.ropeName){ summary.rope.textContent = '02 ' + state.ropeName; summary.rope.removeAttribute('hidden'); }
        else summary.rope.setAttribute('hidden','');
      }
      if (summary.coin){
        if (state.coinTouched) {
          if (state.coin && state.coin !== 'STANDARD') {
            summary.coin.textContent = '03 ' + state.coin;
          } else {
            summary.coin.textContent = '03 Standard';
          }
          summary.coin.removeAttribute('hidden');
        } else {
          summary.coin.setAttribute('hidden','');
        }
      }
    }

    // --- option-card click delegation (per strip) ---
    if (woodOptsHost){
      woodOptsHost.addEventListener('click', function(e){
        var b = e.target.closest('.cway');
        if (!b || !woodOptsHost.contains(b)) return;
        e.preventDefault();
        state.wood     = b.getAttribute('data-qb-wood') || b.getAttribute('data-cway');
        state.woodName = b.getAttribute('data-qb-name') || state.wood;
        woodOptsHost.querySelectorAll('.cway').forEach(function(x){
          x.classList.toggle('active', x === b);
        });
        refreshSummary();
        setTimeout(function(){ showStep('rope'); }, 240);
      });
    }
    if (ropeOptsHost){
      ropeOptsHost.addEventListener('click', function(e){
        var b = e.target.closest('.rope');
        if (!b || !ropeOptsHost.contains(b)) return;
        e.preventDefault();
        state.rope     = b.getAttribute('data-qb-rope') || b.getAttribute('data-rope');
        state.ropeName = b.getAttribute('data-qb-name') || state.rope;
        ropeOptsHost.querySelectorAll('.rope').forEach(function(x){
          x.classList.toggle('active', x === b);
        });
        refreshSummary();
        setTimeout(function(){ showStep('coin'); }, 240);
      });
    }

    // --- coin select (per strip) ---
    (function bindCoinSelect(){
      var sel = coinHost && coinHost.querySelector('[data-qb-coin-select], select');
      var bg  = coinHost && coinHost.querySelector('[data-qb-coin-bg], .coin-input-bg');
      var wrap = coinHost && coinHost.querySelector('.coin-select-wrap');
      var preview = coinHost && coinHost.querySelector('.coin-preview');
      if (!sel) return;
      // Charlie 2026-06-04: defensive coin-select sync.
      // Setting sel.value to 'DAD' silently fails (and falls back to the
      // first option) if the DAD <option> isn't present in the cloned
      // select — which can happen if the BYC clone races against a
      // stale browser cache of the BYC HTML. Guard: verify the option
      // exists; if not, inject it at the top so state.coin always wins.
      var wantedCoin = state.coin || 'DAD';
      var hasOption = Array.prototype.some.call(sel.options, function(o){
        return o.value === wantedCoin;
      });
      if (!hasOption && wantedCoin) {
        var opt = document.createElement('option');
        opt.value = wantedCoin;
        opt.textContent = wantedCoin;
        sel.insertBefore(opt, sel.firstChild);
      }
      sel.value = wantedCoin;
      // If the assignment still failed (shouldn't, but be paranoid), fall
      // back to the actual selected value rather than the wishful state.
      if (sel.value !== wantedCoin) {
        state.coin = sel.value;
      }
      var seedIsStandard = (sel.value === 'STANDARD');
      if (bg) {
        if (seedIsStandard) {
          bg.removeAttribute('src'); bg.classList.add('faded');
        } else {
          bg.src = coinUrl(sel.value); bg.classList.remove('faded');
        }
      }
      if (wrap) wrap.classList.toggle('is-personalised', !seedIsStandard);

      // Charlie 2026-06-01: full coin-swap choreography mirroring the BYC
      // master (is-swapping → is-entering → is-settled + ember sweep).
      // CSS lives in assets/quick-build.css "Coin preview swap choreography".
      var __qbSwapInFlight = false;
      var __qbPendingId    = null;
      function qbCoinSwap(id){
        if (!bg) return;
        if (__qbSwapInFlight) { __qbPendingId = id; return; }
        __qbSwapInFlight = true;
        var src = coinUrl(id);
        var pre = new Image();
        var safety = setTimeout(begin, 220);
        pre.onload = pre.onerror = begin;
        pre.src = src;
        function begin(){
          pre.onload = pre.onerror = null;
          clearTimeout(safety);
          bg.classList.remove('faded','is-revealing','is-entering','is-settled');
          if (preview) preview.classList.remove('is-coin-entering','is-coin-settled');
          var hadSrc = !!bg.getAttribute('src');
          if (hadSrc) {
            bg.classList.add('is-swapping');
            if (preview) preview.classList.add('is-coin-swapping');
          }
          setTimeout(function(){
            bg.setAttribute('src', src);
            bg.classList.remove('is-swapping');
            bg.classList.add('is-entering');
            if (preview) {
              preview.classList.remove('is-coin-swapping');
              preview.classList.add('is-coin-entering');
            }
            void bg.offsetWidth;
            bg.classList.add('is-settled');
            if (preview) preview.classList.add('is-coin-settled');
            setTimeout(function(){
              bg.classList.remove('is-entering','is-settled');
              if (preview) preview.classList.remove('is-coin-entering','is-coin-settled');
              __qbSwapInFlight = false;
              if (__qbPendingId && __qbPendingId !== id) {
                var next = __qbPendingId; __qbPendingId = null;
                qbCoinSwap(next);
              } else {
                __qbPendingId = null;
              }
            }, 480);
          }, hadSrc ? 220 : 0);
        }
      }

      sel.addEventListener('change', function(){
        var v = sel.value || 'DAD';
        var isStandard = (v === 'STANDARD');
        state.coin = v;
        state.coinTouched = true;
        if (bg){
          if (isStandard) {
            bg.removeAttribute('src');
            bg.classList.add('faded');
            bg.classList.remove('is-revealing','is-swapping','is-entering','is-settled');
            if (preview) preview.classList.remove('is-coin-swapping','is-coin-entering','is-coin-settled');
          } else {
            qbCoinSwap(v);
          }
        }
        if (wrap) wrap.classList.toggle('is-personalised', !isStandard);
        refreshSummary();
        refreshBuildBtn();
      });
    })();

    // --- back button + summary jumps ---
    if (backBtn){
      backBtn.addEventListener('click', function(){
        var idx = stepOrder.indexOf(currentStep);
        if (idx > 0) showStep(stepOrder[idx - 1]);
        else closeStrip();
      });
    }
    var summaryHost = strip.querySelector('[data-qb-summary]');
    if (summaryHost){
      summaryHost.addEventListener('click', function(e){
        var pill = e.target.closest('[data-qb-jump]');
        if (!pill || pill.hasAttribute('hidden')) return;
        e.preventDefault();
        var target = pill.getAttribute('data-qb-jump');
        if (target && stepEls[target]) showStep(target);
      });
    }

    // --- open / close ---
    function openStrip(){
      strip.removeAttribute('hidden');
      strip.setAttribute('aria-hidden', 'false');
      // Reset state on every open so the user always starts clean.
      state.wood = null; state.woodName = null;
      state.rope = null; state.ropeName = null;
      // Charlie 2026-06-04 (Mike directive): default coin is DAD
      // (was 'A'). Must match the initial state declared on line ~185
      // and the BYC dropdown's `selected` attribute, otherwise opening
      // the strip silently overrides the page-load default.
      state.coin = 'DAD';
      state.coinTouched = false;
      clearClonedSelections();
      refreshSummary();
      refreshBuildBtn();
      void strip.offsetHeight;
      strip.classList.add('qb-open');
      showStep('wood');
    }
    function closeStrip(){
      strip.classList.remove('qb-open');
      setTimeout(function(){
        strip.setAttribute('hidden', '');
        strip.setAttribute('aria-hidden', 'true');
      }, 600);
    }

    // --- BUILD button: animation → reveal paired BYC → apply selections ---
    function revealHiddenByc(){
      hiddenByc.style.display = '';
      hiddenByc.removeAttribute('hidden');
      hiddenByc.removeAttribute('aria-hidden');
      hiddenByc.removeAttribute('data-build-hidden');
      hiddenByc.setAttribute('data-build-revealed', '1');
    }
    // Charlie 2026-06-05 v4 (Mike): when the quickbuilder finishes and
    // we reveal the BYC, scroll to the IMAGE GALLERY inside the BYC.
    //
    // The big complication: the strip's closeStrip() collapse animation
    // takes ~600ms, and while it's collapsing the BYC gallery's y-coordinate
    // is moving upward by the strip's height (~500px). Previous v3 waited
    // 1400ms (until collapse fully done) then teleported — felt frozen for
    // the user. v2 did 3 scrolls and the user saw the third one re-correct
    // backward as the strip finished collapsing.
    //
    // SOLUTION (v4): predict the gallery's FINAL position by adjusting the
    // measured current position for the strip's still-to-collapse height,
    // then do exactly ONE instant scroll. The strip's collapse animation
    // runs in parallel — by the time the user's eye moves, both the
    // strip and the scroll have landed at the same final state.
    function getGalleryFinalY(){
      var gallery = hiddenByc.querySelector('.pdp-gallery') || hiddenByc;
      var topbarH = window.innerWidth < 760
        ? (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h-mobile')) || 56)
        : (parseInt(getComputedStyle(document.documentElement).getPropertyValue('--topbar-h')) || 36);
      var rect = gallery.getBoundingClientRect();
      // Subtract strip height if the strip is currently above the gallery
      // and is in the middle of its collapse (i.e. still has visible height).
      // After collapse, strip becomes display:none / hidden — zero height.
      var stripAdjust = 0;
      var stripRect = strip.getBoundingClientRect();
      var galleryTop = rect.top + window.scrollY;
      var stripTop = stripRect.top + window.scrollY;
      if (stripRect.height > 0 && stripTop < galleryTop) {
        // Strip is above the gallery and still visible — about to collapse.
        // Its full height will be removed from the layout, so the gallery
        // will move up by that amount once the collapse finishes.
        stripAdjust = stripRect.height;
      }
      return Math.max(0, galleryTop - stripAdjust - topbarH - 12);
    }
    function scrollToRevealedGallery(){
      // Wait one frame for the BYC reveal to take effect, then instant-jump
      // to the predicted final gallery position. The strip's CSS-driven
      // collapse animation runs concurrently and finishes by the time the
      // user's eye settles.
      requestAnimationFrame(function(){
        requestAnimationFrame(function(){
          try { window.scrollTo({ top: getGalleryFinalY(), behavior: 'auto' }); }
          catch(_) { window.scrollTo(0, getGalleryFinalY()); }
        });
      });
    }
    function applySelections(){
      if (state.wood){
        var woodBtn = hiddenByc.querySelector('[data-cway="' + state.wood + '"]');
        if (woodBtn) fireClick(woodBtn);
      }
      if (state.rope){
        var ropeBtn = hiddenByc.querySelector('[data-rope="' + state.rope + '"]');
        if (ropeBtn) fireClick(ropeBtn);
      }
      var coinSel = hiddenByc.querySelector('[data-coin-select]');
      if (coinSel){
        coinSel.value = state.coin || 'DAD';
        fireChange(coinSel);
      }
    }

    if (buildBtn){
      buildBtn.addEventListener('click', function(){
        if (buildBtn.hasAttribute('disabled')) return;
        if (!building) {
          revealHiddenByc();
          requestAnimationFrame(applySelections);
          scrollToRevealedGallery();
          closeStrip();
          return;
        }
        building.removeAttribute('hidden');
        void building.offsetHeight;
        building.classList.add('is-visible');
        buildBtn.setAttribute('disabled', '');

        setTimeout(function(){
          revealHiddenByc();
          requestAnimationFrame(function(){
            applySelections();
            building.classList.remove('is-visible');
            setTimeout(function(){ building.setAttribute('hidden', ''); }, 280);
            scrollToRevealedGallery();
            setTimeout(closeStrip, 700);
          });
        }, ANIM_MS);
      });
    }

    // --- bind triggers for this instance ---
    function bindTrigger(el){
      if (!el || el.__qbBound) return;
      el.__qbBound = true;
      el.setAttribute('data-qb-bound', '1');
      el.addEventListener('click', function(e){
        // For <a> elements we cancel navigation; for <button> the
        // preventDefault is a harmless no-op.
        e.preventDefault();
        if (strip.classList.contains('qb-open')) {
          strip.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          openStrip();
          setTimeout(function(){
            strip.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 60);
        }
      });
    }

    // Explicit triggers by instance key.
    if (instanceKey) {
      document.querySelectorAll('[data-qb-trigger="' + CSS.escape(instanceKey) + '"]').forEach(bindTrigger);
    }
    // Backwards-compat: hero CTA(s) always open the FIRST strip on the page.
    if (isFirstStrip) {
      document.querySelectorAll('.hero a[href="#build"], .hero a[href*="#build"]').forEach(function(a){
        a.setAttribute('data-qb-trigger-hero', '1');
        bindTrigger(a);
      });
    }

    // Expose minimal handle for debugging.
    strip.__qb = { state: state, openStrip: openStrip, closeStrip: closeStrip, hiddenByc: hiddenByc };
  }

  // ------------------------------------------------------------------
  // Ad-hoc CTA rerouter (Charlie 2026-05-21).
  // ------------------------------------------------------------------
  // The homepage now renders THREE Build-Your-CHUUG instances:
  //   1. hero hidden BYC      (paired with quick_build_hero)
  //   2. gallery hidden BYC   (paired with quick_build_social)
  //   3. bottom visible BYC   (the real `#build` configurator)
  //
  // Any CTA on the page that scrolls to BYC ("BUILD", "BUILD YOURS",
  // "GET TWO", or any other `#build` anchor link) should be rerouted to
  // the NEAREST of those three sections in DOM order, with the "Below"
  // tiebreaker — i.e. when a CTA sits between two BYCs, prefer the BYC
  // that follows it in document order.
  //
  // If the chosen BYC is one of the two hidden ones, we scroll to its
  // paired Quick Build STRIP and open the strip (showing step 1 — Wood)
  // as if the user had clicked the Quick Build trigger CTA themselves.
  //
  // EXCLUDED (left untouched — already wired or intentionally sticky):
  //   - Hero "Build Yours" (inside `.hero`; auto-bound to qb-hero)
  //   - Social-proof gallery "BUILD YOURS" (`[data-qb-trigger="social"]`)
  //   - Sticky header chip (`.header-cta-chip`)
  //   - Sticky mobile buy bar (`.sticky-buy-bar`)
  //
  // The bottom-of-page anchor section CTA IS rerouted — but the nearest
  // BYC below it is the bottom visible BYC, so the effective behaviour is
  // unchanged (smooth scroll to the real configurator).
  // ------------------------------------------------------------------
  function routeAdHocCtas(){
    var bycSections = Array.prototype.slice.call(
      document.querySelectorAll('[data-section-id][id="build"]')
    );
    // Single-BYC pages (PDPs, /pages/*, etc.) keep default browser
    // hash-scroll behaviour. The rerouter only activates when there's
    // genuine ambiguity to resolve.
    if (bycSections.length < 2) return;

    // Build the BYC table (document order; querySelectorAll returns in
    // document order so the array is already sorted correctly).
    var bycs = bycSections.map(function(byc){
      var rec = {
        el: byc,
        sectionId: byc.getAttribute('data-section-id')
      };
      // Find paired Quick Build strip:
      //   1) explicit `data-qb-target-byc="<section-id>"` match
      //   2) DOM-order fallback — the last strip that precedes this BYC
      var strips = document.querySelectorAll('[data-quick-build]');
      var matched = null;
      for (var i = 0; i < strips.length; i++) {
        if (strips[i].getAttribute('data-qb-target-byc') === rec.sectionId) {
          matched = strips[i]; break;
        }
      }
      if (!matched) {
        for (var j = 0; j < strips.length; j++) {
          var pos = strips[j].compareDocumentPosition(byc);
          if (pos & Node.DOCUMENT_POSITION_FOLLOWING) matched = strips[j];
        }
      }
      rec.qbStrip    = matched;
      rec.qbInstance = matched ? matched.getAttribute('data-qb-instance') : null;
      return rec;
    });

    var EXCLUDE_ANCESTOR = '.hero, .sp-qb-cta-row, .header-cta-chip, .sticky-buy-bar';

    var ctas = document.querySelectorAll('a[href="#build"], a[href$="#build"]');
    Array.prototype.forEach.call(ctas, function(cta){
      // Skip CTAs already wired as Quick Build triggers.
      if (cta.hasAttribute('data-qb-bound')) return;
      if (cta.hasAttribute('data-qb-trigger-hero')) return;
      if (cta.hasAttribute('data-qb-trigger')) return;
      // Skip CTAs inside excluded containers.
      if (cta.closest(EXCLUDE_ANCESTOR)) return;
      // Don't double-bind.
      if (cta.__qbRerouterBound) return;
      cta.__qbRerouterBound = true;
      cta.setAttribute('data-qb-bound', '1');

      cta.addEventListener('click', function(e){
        // Pick the first BYC whose document position FOLLOWS this CTA
        // ("Below" tiebreaker). Fall back to the LAST BYC above if none below.
        var chosen = null;
        for (var i = 0; i < bycs.length; i++) {
          var p = cta.compareDocumentPosition(bycs[i].el);
          if (p & Node.DOCUMENT_POSITION_FOLLOWING) { chosen = bycs[i]; break; }
        }
        if (!chosen) chosen = bycs[bycs.length - 1];

        var isHidden = chosen.el.hasAttribute('data-build-hidden');

        if (isHidden && chosen.qbStrip && chosen.qbStrip.__qb) {
          // Scroll to the Quick Build strip + open it (step 1 — Wood),
          // exactly as if the user had clicked its trigger CTA themselves.
          e.preventDefault();
          chosen.qbStrip.__qb.openStrip();
          setTimeout(function(){
            try { chosen.qbStrip.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
            catch(_) { chosen.qbStrip.scrollIntoView(); }
          }, 60);
        } else if (isHidden && chosen.qbInstance) {
          // Quick Build strip not yet initialised — fall back to dispatching
          // a click on its trigger button (which will open + scroll the strip).
          e.preventDefault();
          var trig = document.querySelector(
            '[data-qb-trigger="' + chosen.qbInstance + '"]'
          );
          if (trig) fireClick(trig);
        } else {
          // Visible BYC — smooth scroll to it. (Default browser jump would
          // land on the FIRST #build element on the page, which is one of
          // the hidden duplicates, so we always intercept.)
          e.preventDefault();
          try { chosen.el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
          catch(_) { chosen.el.scrollIntoView(); }
        }
      }, false);
    });

    // Safety net for any unbound #build link that bubbles up (e.g. a CTA
    // added to the page dynamically after init). Routes to the LAST visible
    // BYC, which is the real configurator at the bottom of the homepage.
    document.addEventListener('click', function(e){
      var a = e.target.closest && e.target.closest('a[href="#build"], a[href$="#build"]');
      if (!a) return;
      if (a.hasAttribute('data-qb-bound') || a.hasAttribute('data-qb-trigger-hero')) return;
      if (a.hasAttribute('data-qb-trigger')) return;
      var stillHidden = document.querySelector('[data-build-hidden="1"]');
      if (!stillHidden) return;
      var target = null;
      for (var k = bycs.length - 1; k >= 0; k--) {
        if (!bycs[k].el.hasAttribute('data-build-hidden')) { target = bycs[k].el; break; }
      }
      if (!target) return;
      e.preventDefault();
      try { target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      catch(_) { target.scrollIntoView(); }
    });
  }

  // ----------------------------------------------------------------
  // Hash-based auto-open. When the page loads with #qb-hero (set by
  // the off-homepage header BUILD chip and the BUILD YOUR CHUUG nav
  // link), open the hero Quick Build strip automatically after init.
  // 2026-05-21 Mike.
  // ----------------------------------------------------------------
  function handleHeroHashOpen(){
    if (window.location.hash !== '#qb-hero') return;
    var heroStrip = document.querySelector('[data-qb-instance="hero"]');
    if (!heroStrip || !heroStrip.__qb) return;
    // Clean the hash so a refresh doesn't keep reopening (and so the
    // browser doesn't try to scroll to an element with that id).
    try { history.replaceState(null, '', window.location.pathname + window.location.search); } catch(_) {}
    setTimeout(function(){
      try { heroStrip.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
      catch(_) { heroStrip.scrollIntoView(); }
      setTimeout(function(){ heroStrip.__qb.openStrip(); }, 220);
    }, 60);
  }

  // ----------------------------------------------------------------
  // Off-homepage "BUILD YOUR CHUUG" nav link interceptor. Shopify
  // merchants set this link's URL in Admin → Navigation (usually `/`
  // or `/#build`). When we're NOT on the homepage and the user clicks
  // a link whose label matches "build your chuug" (case-insensitive)
  // OR whose href is the homepage with #build, redirect to /#qb-hero
  // so the homepage loads with the strip auto-opening.
  // ----------------------------------------------------------------
  function bindNavBuildIntercept(){
    var heroPresent = !!document.querySelector('[data-qb-instance="hero"]');
    if (heroPresent) return; // on homepage — routeAdHocCtas handles it
    document.addEventListener('click', function(e){
      var a = e.target.closest && e.target.closest('a[href]');
      if (!a) return;
      var href = (a.getAttribute('href') || '').trim();
      // Normalise label: lowercase, collapse all whitespace (including
      // \u00A0 non-breaking spaces and arbitrary inner-element padding).
      var label = (a.textContent || '').toLowerCase().replace(/\s+/g,' ').trim();
      // True for any visible "build your chuug" wording regardless of
      // arrow suffixes, smallcaps spans, etc.
      var textMatch = label.indexOf('build your chuug') !== -1;
      // Match common href shapes Shopify nav admins might set:
      //   /                       (homepage root, link relies on label)
      //   /#build                 (direct anchor to BYC)
      //   /#qb-hero               (already canonical — still intercept to land cleanly)
      //   https://chuug.com/...   (absolute homepage URL)
      //   /collections/...        (NEVER match — collection nav links)
      var hrefIsHomepage =
        href === '/' ||
        href === '/#build' ||
        href === '/#qb-hero' ||
        /^https?:\/\/[^/]+\/?(\#(build|qb-hero))?$/i.test(href);
      var looksLikeBuildLink = textMatch || (hrefIsHomepage && label.indexOf('build') !== -1);
      if (!looksLikeBuildLink) return;
      e.preventDefault();
      e.stopPropagation();
      window.location.href = '/#qb-hero';
    }, true);
  }

  ready(function init(){
    var strips = document.querySelectorAll('[data-quick-build]');
    if (!strips.length) {
      // No strips on this page — we're off-homepage. Intercept the
      // BUILD YOUR CHUUG nav link so it navigates to /#qb-hero instead
      // of the homepage root, where the hero quick-build will open.
      bindNavBuildIntercept();
      return;
    }

    strips.forEach(function(strip, idx){
      initStrip(strip, idx === 0);
    });

    // After all strips have wired up their triggers (and stamped
    // data-qb-bound / data-qb-trigger-hero markers), run the ad-hoc
    // rerouter so the remaining #build CTAs land on the nearest BYC.
    routeAdHocCtas();

    // Finally, check for #qb-hero in the URL and auto-open the hero strip.
    handleHeroHashOpen();
  });
})();
